import { HbrIcon } from "@harbor/elements-react-standalone";
import { alertsWarningSmallBorder } from "@harbor/elements/icons";
import React, { useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import { useFetchApprovalDetails } from "../../hooks/approval-request-hooks";
import { checkApprovalTemplatesLoaded } from "../../support/slices/approvalTemplatesSlice";
import { RootState, useAppDispatch } from "../../support/store";
import { ApprovalRequestType } from "../../types/approval-request";
import { titleCase } from "../security-toolbox-ui/security-toolbox-ui-utils";
import StbApiModal from "../security-toolbox-ui/StbApiModal";


interface ApprovalRequestModalProps {
  children?: React.ReactNode;
  open: boolean;
  requestItemType: ApprovalRequestType;
  requestTitle?: string;
  requestObjectIds?: Record<string, string | number>;
  handleClose?: () => Promise<void>;
  handleRequest?: () => Promise<void>;
  submitDisabled?: boolean;
}

const ApprovalRequestModal: React.FC<ApprovalRequestModalProps> = ({
  children,
  open = false,
  requestItemType,
  requestTitle,
  requestObjectIds = {},
  handleClose = async () => {},
  handleRequest = async () => {},
  submitDisabled = false,
}) => {

    // REQUESTER
  const { name: requester, email: requesterEmail, id: requesterId } = useSelector((state: RootState) => state.user.userData);

  // APPROVER
  const { name: approver, email: approverEmail } = useSelector((state: RootState) => state.user.manager);
  
    // TEMPLATE
  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(checkApprovalTemplatesLoaded());
  }, [dispatch]);
  const template = useSelector((state: RootState) => state.approvalTemplates.templatesLookup[requestItemType]);

    // REQUEST DETAILS
  const { data: requestDetails, failedSources } = useFetchApprovalDetails(
    requestItemType,
    Object.keys(template?.sections ?? {}),
    requestObjectIds,
    { enabled: !!template && !!requestObjectIds }
  );

  const requestFragments = useMemo(() => {
    if (!template || !requestDetails) return [];

    const getValue = (data: Record<string, any>, idPath: (string | number)[]) => {
      const value = idPath.reduce((obj, key) => (obj && obj[key] !== undefined ? obj[key] : null), data);
      return value && value.trim() !== '' ? value : (<span className='italic'>No value</span>);
    };

    const allItems = Object.entries(template.sections).flatMap(([sectionKey, items]) =>
      items.map(item => ({ sectionKey, item }))
    );

    allItems.sort((a, b) => a.item.modalOrder - b.item.modalOrder);

    return allItems.map(({ sectionKey, item }) => {
      switch (item.type) {
        case 'field':
          return (
            <div key={`${sectionKey}-${item.id.join('-')}`} className='col-span-2 grid grid-cols-[10rem_1fr] gap-x-4 mb-2'>
              <div className='text-sm font-semibold'>{item.label}:</div>
              <div className='text-sm text-gray-700'>{getValue(requestDetails[sectionKey], item.id)}</div>
            </div>
          );

        case 'group':
          return (
            <div key={`${sectionKey}-${item.label}`} className='mb-2'>
              <div className='text-sm font-semibold'>{item.label}:</div>
              {item.fields.map(field => (
                <div key={field.id.join('-')} className='col-span-2 grid grid-cols-[10rem_1fr] gap-x-4'>
                  <div className='text-sm font-semibold ml-4'>{field.label}:</div>
                  <div className='text-sm text-gray-700'>{getValue(requestDetails[sectionKey], field.id)}</div>
                </div>
              ))}
            </div>
          );

        case 'collection':
          return (
            <div key={`${sectionKey}-${item.label}`} className='mb-2'>
              <div className='text-sm font-semibold'>{item.label}:</div>
              {requestDetails[sectionKey]?.length === 0
                ? <div className='text-sm text-gray-700 italic ml-4'>No items</div>
                : <ul className='list-disc ml-8'>
                    {(requestDetails[sectionKey] ?? []).map((record, idx) => (
                      <li key={idx} className='text-sm text-gray-700'>{getValue(record, item.fields[0].id)}</li>
                    ))}
                  </ul>
              }
            </div>
          );
      }
    });
  }, [template, requestDetails]);
  
  return (
    <StbApiModal
      title={`Request ${titleCase(requestItemType)} Approval`}
      open={open}
      submitLabel="Send Request"
      submitButtonRole="request"
      submitSuccess="Request submitted"
      onSubmit={handleRequest}
      onClose={handleClose}
      submitDisabled={submitDisabled}
    >
      {failedSources.length > 0 && (
        <div className="border border-amber-300 bg-amber-50 rounded-md p-3 mb-4">
          <div className="flex items-start gap-2">
            <HbrIcon asset={alertsWarningSmallBorder} size="20px" sentiment="warning" />
            <div className="text-sm text-amber-900">
              <div className="font-semibold">Some contextual data could not be loaded.</div>
              <div>You can continue with this request, but review the details carefully.</div>
              <ul className="list-disc ml-5 mt-2">
                {failedSources.map((sourceError) => (
                  <li key={sourceError.key}>{sourceError.message}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="border border-gray-300 rounded-md p-2 mb-4">
        <div className="grid grid-cols-[10rem_1fr] gap-x-4">
          <div className="text-sm font-semibold">Request Title:</div>
          <div className="text-sm text-gray-700 mb-2">{requestTitle ?? `Request ${requestItemType} Approval`}</div>

          <div className="text-sm font-semibold">Requester:</div>
          <div className="text-sm text-gray-700">{requester ?? ''}</div>

          <div className="text-sm font-semibold">Approver:</div>
          <div className="text-sm text-gray-700 mb-2">{approver ?? ''}</div>
        </div>

        {requestFragments.map((fragment, idx) => (fragment))}
      </div>
      {children}
    </StbApiModal>
  )
}

export default ApprovalRequestModal;
